import React, { useState } from 'react';
import axios from 'axios';
import { Download, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

const SubmissionRow = ({ learner, programmeId, token, onGraded }) => {
  const sub = learner.final_submission;
  const [grade, setGrade] = useState(sub?.grade_percentage ?? '');
  const [feedback, setFeedback] = useState(sub?.feedback || '');
  const [saving, setSaving] = useState(false);
  const id = learner.enrollment_id;

  const save = async () => {
    const value = Number(grade);
    if (grade === '' || Number.isNaN(value) || value < 0 || value > 100) {
      toast.error('Enter a grade between 0 and 100');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/programmes/${programmeId}/poe/${sub.submission_id}/grade`, { grade_percentage: value, feedback }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Graded ${learner.name || learner.email}: ${value}%`);
      onGraded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save grade');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-4 space-y-3" data-testid={`grade-row-${id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm text-white">{learner.name || learner.email}</p>
          <p className="text-xs text-zinc-500">{learner.email}</p>
        </div>
        {!sub ? (
          <Badge className="bg-zinc-700/40 text-zinc-400" data-testid={`grade-status-${id}`}>Not submitted</Badge>
        ) : (
          <div className="flex items-center gap-2">
            <Badge className={sub.status === 'graded' ? 'bg-green-500/15 text-green-400' : 'bg-amber-500/15 text-amber-300'} data-testid={`grade-status-${id}`}>
              {sub.status === 'graded' ? `Graded ${sub.grade_percentage}%` : 'Awaiting grade'}
            </Badge>
            <a
              href={`${API}/programmes/${programmeId}/poe/${sub.submission_id}/download?auth=${token}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-teal-400 hover:text-teal-300"
              data-testid={`grade-download-${id}`}
            >
              <Download className="h-3.5 w-3.5" /> {sub.filename || 'Download'}
            </a>
          </div>
        )}
      </div>
      {sub && (
        <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_auto] gap-3 items-start">
          <Input type="number" min={0} max={100} placeholder="Grade %" value={grade} onChange={(e) => setGrade(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`grade-input-${id}`} />
          <Textarea rows={2} placeholder="Feedback for the learner" value={feedback} onChange={(e) => setFeedback(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid={`grade-feedback-${id}`} />
          <Button disabled={saving} onClick={save} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`grade-save-${id}`}>
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default function GradeFinalAssignmentDialog({ open, onOpenChange, learners, programmeId, token, assignmentTitle, onGraded }) {
  const sorted = [...(learners || [])].sort((a, b) => {
    const rank = (l) => (!l.final_submission ? 2 : l.final_submission.status === 'graded' ? 1 : 0);
    return rank(a) - rank(b);
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="grade-final-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Grade final integrated assignment</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {assignmentTitle || 'Final assignment'} — grades count towards the 100-point rubric immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!sorted.length ? (
            <p className="text-sm text-zinc-500" data-testid="grade-no-learners">No enrolled learners yet.</p>
          ) : sorted.map((l) => (
            <SubmissionRow key={`${l.enrollment_id}-${l.final_submission?.status}`} learner={l} programmeId={programmeId} token={token} onGraded={onGraded} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
